import { LOS_DATA_TEST_ID } from '@e2e/constants/exam-management';
import { findLevelItemOrderById } from '@e2e/utils';
import { QBankStructureLevelTag } from '@exam-management/components';
import { QBankStructureStatus } from '@exam-management/graphql';
import { QBankStructureLevelCategory } from '@exam-management/qbs-provider';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AddIcon,
  Box,
  Button,
  Divider,
  ExpandMoreRoundedIcon,
  Flex,
  Rotate,
  styled,
  Text,
  useTheme,
  type Theme,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useHasPermission } from '@shared/permissions';

import { ADD_LOS_REQUIRED_PERMISSIONS, FALLBACK_LEVEL_NAME } from '../../constants';
import { getBackgroundColor, type QBankStructureLevelItem } from '../../utils';
import { LOSList, type LOSListSlots } from '../los-list';

function getAccordionSummarySx(
  theme: Theme,
  isOpen: boolean,
  isPart: boolean,
  collapseLevel: number,
  bgColor: string,
) {
  return {
    '&.Mui-focusVisible': {
      backgroundColor: isOpen ? bgColor : theme.palette.common.white,
    },
    '& .MuiAccordionSummary-content': {
      m: theme.spacing(0),
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      mx: theme.spacing(3),
    },
    flexDirection: 'row-reverse',
    height: '52px',
    p: theme.spacing(0),
    borderBottom: isOpen ? `1px solid ${theme.palette.grey[300]}` : 'none',
    borderTop: collapseLevel > 1 ? `1px solid ${theme.palette.grey[300]}` : 'none',
    bgcolor: isOpen ? bgColor : theme.palette.common.white,
    color: isOpen && isPart ? theme.palette.background.paper : theme.palette.text.primary,
  };
}

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 0, 0, 4),
  height: 'fit-content',
}));

export interface ChapterValue {
  id: string;
  name: string;
}

export interface LOSCollapseProps {
  data: QBankStructureLevelItem;
  status: QBankStructureStatus;
  collapseLevel?: number;
  qBankStructureLevelItems: QBankStructureLevelItem[];
  qBankStructureInformationCode: number;
  onAddLOS?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, chapterId: string) => void;
  slots?: {
    losList?: LOSListSlots;
  };
}

export default function LOSCollapse(props: Readonly<LOSCollapseProps>) {
  const {
    data,
    status,
    collapseLevel = 0,
    qBankStructureLevelItems,
    qBankStructureInformationCode,
    onAddLOS,
    slots,
  } = props;
  const { id: levelId, qBankStructureLevel, children, los: losData } = data;
  const { category } = qBankStructureLevel;
  const losList = losData ?? [];

  const hasAbilityToAddLOS = useHasPermission(ADD_LOS_REQUIRED_PERMISSIONS);

  const [isOpen, { toggle }] = useDisclosure(true);
  const theme = useTheme();

  const hasChildren = children && children.length > 0;
  const isRootLevel = collapseLevel === 0;
  const isPart = category === QBankStructureLevelCategory.Part;
  const shouldDisplayAddLOSButton =
    hasAbilityToAddLOS &&
    category === QBankStructureLevelCategory.Chapter &&
    [QBankStructureStatus.Draft, QBankStructureStatus.Unpublished].includes(status);

  const bgColor = getBackgroundColor(category, theme);
  const addButtonLOSDataTestIdIdentifier = findLevelItemOrderById(
    qBankStructureLevelItems,
    levelId,
    qBankStructureInformationCode,
  );

  return (
    <Box
      sx={(theme) => ({
        borderRadius: isRootLevel ? theme.shape.rounded(2) : theme.shape.rounded(0),
        border: isRootLevel ? `1px solid ${theme.palette.grey[300]}` : 'none',
        borderLeft: `1px solid ${theme.palette.grey[300]}`,
        overflow: 'hidden',
      })}
    >
      <Accordion
        disableGutters
        elevation={0}
        square
        expanded={isOpen}
        onChange={toggle}
        sx={{
          overflow: 'hidden',
          height: 'fit-content',
        }}
      >
        <AccordionSummary
          component={Flex}
          expandIcon={
            <Rotate in={false}>
              <ExpandMoreRoundedIcon
                sx={(theme) => ({
                  color: isPart && isOpen ? theme.palette.common.white : theme.palette.text.primary,
                })}
              />
            </Rotate>
          }
          sx={getAccordionSummarySx(theme, isOpen, isPart, collapseLevel, bgColor)}
        >
          <Flex
            sx={(theme) => ({
              py: theme.spacing(3.5),
              gap: theme.spacing(2),
              width: '100%',
              alignItems: 'center',
            })}
          >
            <QBankStructureLevelTag category={category} />
            <Divider orientation='vertical' variant='middle' flexItem />
            <Text variant='body1Semibold'>{data.tag?.name ?? FALLBACK_LEVEL_NAME}</Text>
          </Flex>
          {shouldDisplayAddLOSButton ? (
            <Button
              fullWidth
              size='small'
              data-testid={`${LOS_DATA_TEST_ID.LEVEL_ITEM_ROW}-${addButtonLOSDataTestIdIdentifier}-add-los-button`}
              variant='outlined'
              color='secondary'
              startIcon={<AddIcon />}
              sx={{ m: (theme) => theme.spacing(2.5, 2), maxWidth: '102px' }}
              onClick={(event) => {
                event.stopPropagation();
                onAddLOS?.(event, levelId);
              }}
            >
              Add LOS
            </Button>
          ) : null}
        </AccordionSummary>

        {hasChildren ? (
          // recursive component if still has children
          <StyledAccordionDetails>
            {children?.map((childData, index) => {
              return (
                <LOSCollapse
                  key={childData.id}
                  {...props}
                  data={childData}
                  collapseLevel={index + 1}
                />
              );
            })}
          </StyledAccordionDetails>
        ) : (
          // render los list if no children left
          <LOSList data={losList} slots={slots?.losList} />
        )}
      </Accordion>
    </Box>
  );
}
